import { createFileRoute } from "@tanstack/react-router";
import {
  HelpCircle,
  Book,
  MessageCircle,
  ExternalLink,
  FileText,
} from "lucide-react";

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/help")({
  component: HelpPage,
});

function HelpPage() {
  const helpResources = [
    {
      title: "Documentation",
      description: "Comprehensive guides and API reference",
      icon: Book,
      action: "View Docs",
      link: "#",
    },
    {
      title: "Support Chat",
      description: "Get real-time help from our support team",
      icon: MessageCircle,
      action: "Start Chat",
      link: "#",
    },
    {
      title: "Community Forum",
      description: "Connect with other users and share solutions",
      icon: ExternalLink,
      action: "Visit Forum",
      link: "#",
    },
    {
      title: "API Reference",
      description: "Complete API documentation and examples",
      icon: FileText,
      action: "View API",
      link: "#",
    },
  ];

  const faqs = [
    {
      question: "How do I create a new tenant?",
      answer:
        'Navigate to the Tenants page and click the "Create Tenant" button. Fill in the required information and save.',
    },
    {
      question: "What does the health check monitor?",
      answer:
        "The health check monitors all critical system components including database, API gateway, and services.",
    },
    {
      question: "How can I configure notifications?",
      answer:
        "Go to Settings > Notifications to configure your alert preferences and notification channels.",
    },
    {
      question: "Where can I find system logs?",
      answer:
        "System logs are available in the Settings section under the Advanced tab.",
    },
  ];

  return (
    <div className="flex h-full w-full flex-col gap-6">
      <div className="flex flex-row items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-[-1%]">
            Help & Support
          </h2>
          <p className="text-muted-foreground">
            Find answers, get support, and learn how to make the most of Harmony
            Management
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {helpResources.map((resource) => (
          <Card
            key={resource.title}
            className="cursor-pointer transition-shadow hover:shadow-md"
          >
            <CardHeader>
              <div className="flex items-center space-x-3">
                <resource.icon className="text-primary h-5 w-5" />
                <CardTitle className="text-lg">{resource.title}</CardTitle>
              </div>
              <CardDescription>{resource.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" size="sm">
                {resource.action}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <HelpCircle className="mr-2 h-5 w-5" />
            Frequently Asked Questions
          </CardTitle>
          <CardDescription>
            Common questions and answers about using the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div
                key={faq.question}
                className="border-b pb-4 last:border-b-0 last:pb-0"
              >
                <h4 className="mb-2 font-medium">{faq.question}</h4>
                <p className="text-muted-foreground text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Need More Help?</CardTitle>
          <CardDescription>
            Can't find what you're looking for? Get in touch with our support
            team
          </CardDescription>
        </CardHeader>
        <CardContent className="flex space-x-4">
          <Button>
            <MessageCircle className="mr-2 h-4 w-4" />
            Contact Support
          </Button>
          <Button variant="outline">
            <ExternalLink className="mr-2 h-4 w-4" />
            Submit Feedback
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
